import React, { useState, useEffect, useContext } from "react";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  limit,
  updateDoc,
} from "firebase/firestore";
import { AuthContext } from "../../AuthProvider";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaUserFriends,
  FaCog,
  FaTrashAlt,
  FaUserPlus,
} from "react-icons/fa";

const TEAMS_COLLECTION = "teams"; // Ensure this matches exactly

const CreateTeam = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [teamName, setTeamName] = useState("");
  const [members, setMembers] = useState([]); // {email, role}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fetchTeams = async () => {
      setLoading(true);
      setError(null);

      try {
        const [own, creator, reader] = await Promise.all([
          getDocs(
            query(
              collection(db, "teams"),
              where("owners", "array-contains", user.uid)
            )
          ),
          getDocs(
            query(
              collection(db, "teams"),
              where("creators", "array-contains", user.email)
            )
          ),
          getDocs(
            query(
              collection(db, "teams"),
              where("readers", "array-contains", user.email)
            )
          ),
        ]);

        const map = new Map();

        own.forEach((d) =>
          map.set(d.id, { id: d.id, ...d.data(), role: "owner" })
        );
        creator.forEach(
          (d) =>
            !map.has(d.id) &&
            map.set(d.id, { id: d.id, ...d.data(), role: "creator" })
        );
        reader.forEach(
          (d) =>
            !map.has(d.id) &&
            map.set(d.id, { id: d.id, ...d.data(), role: "reader" })
        );

        setTeams(Array.from(map.values()));
      } catch (err) {
        console.error("Error fetching teams:", err);
        setError("Failed to load teams.");
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [user]);

  /* ----------------------- member helpers --------------------------- */
  const addEmptyMember = () =>
    setMembers((prev) => [...prev, { email: "", role: "reader" }]);

  const updateMember = (i, k, v) =>
    setMembers((p) => p.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));

  const deleteMember = (i) => {
    const member = members[i];
    if (
      window.confirm(
        `Are you sure you want to remove ${member.email} from the team?`
      )
    ) {
      setMembers((p) => p.filter((_, idx) => idx !== i));
    }
  };

  /* --------------------------- create team --------------------------- */
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) return setError("Team name is required");

    // Enhanced token debugging
    const token = await user?.getIdTokenResult();
    // console.log("Complete Token Debug:", token);

    // console.log("Auth Debug:", {
    //   uid: user?.uid,
    //   email: user?.email,
    //   emailVerified: user?.emailVerified,
    //   isAuthenticated: !!user,
    //   tokenEmail: token?.claims?.email,
    //   metadata: user?.metadata,
    // });

    if (!user?.uid) return setError("You must be logged in to create a team");

    try {
      setLoading(true);
      setError(null);

      // First try to create a minimal team document
      const minimalTeamData = {
        name: teamName.trim(),
        owners: [user.uid],
        creators: [user.email],
        readers: [],
        createdAt: new Date(),
        createdBy: user.uid,
      };

      // Debug collection path
      const teamsCollection = collection(db, TEAMS_COLLECTION);
      // console.log("Collection Path Debug:", {
      //   collectionPath: teamsCollection.path,
      //   fullPath: `${db._databaseId.projectId}/${db._databaseId.database}/documents/${TEAMS_COLLECTION}`,
      //   projectId: db._databaseId.projectId,
      //   databaseId: db._databaseId.database,
      //   collectionId: teamsCollection.id,
      //   parent: teamsCollection.parent?.path || "root",
      // });

      // Verify collection exists
      try {
        const testQuery = query(teamsCollection, limit(1));
        const testSnapshot = await getDocs(testQuery);
        // console.log("Collection Validation:", {
        //   exists:
        //     !testSnapshot.empty || testSnapshot.metadata?.fromCache === false,
        //   path: teamsCollection.path,
        //   size: testSnapshot.size,
        //   metadata: testSnapshot.metadata,
        // });
      } catch (readErr) {
        // console.warn("Collection validation failed:", {
        //   error: readErr.message,
        //   code: readErr.code,
        //   details: readErr.details,
        // });
      }

      // Try creating minimal team first
      // console.log("Attempting to create minimal team:", {
      //   collection: TEAMS_COLLECTION,
      //   data: minimalTeamData,
      // });

      // Verify collection access
      try {
        const testRead = await getDocs(query(teamsCollection, limit(1)));
        // console.log("Collection access test:", {
        //   canRead: true,
        //   empty: testRead.empty,
        //   size: testRead.size,
        // });
      } catch (readErr) {
        // console.warn("Collection read test failed:", readErr);
      }

      // right before addDoc
      // console.log("about to write", JSON.stringify(minimalTeamData, null, 2));

      const teamRef = await addDoc(teamsCollection, minimalTeamData);
      // console.log("Minimal team created successfully with ID:", teamRef.id);

      // If minimal team succeeds, update with full data
      if (members.length > 0) {
        const creatorsSet = new Set([user.email]);
        const readersSet = new Set();

        members.forEach(({ email, role }) => {
          const clean = email?.trim().toLowerCase();
          if (!clean || !/^[^@]+@[^@]+\.[^@]+$/.test(clean)) {
            throw new Error(`Invalid email format: ${email}`);
          }
          if (role === "owner") {
            creatorsSet.add(clean);
          } else {
            readersSet.add(clean);
          }
        });

        const updateData = {
          creators: Array.from(creatorsSet),
          readers: Array.from(readersSet),
        };

        await updateDoc(teamRef, updateData);
        // console.log("Team updated with full member data");
      }

      // Navigate to the team settings page
      navigate(`/ai-sdr/settings/team/${teamRef.id}`);
    } catch (err) {
      // console.error("Detailed error:", {
      //   error: err,
      //   code: err.code,
      //   message: err.message,
      //   details: err.details,
      //   stack: err.stack,
      //   name: err.name,
      //   errorInfo: err.errorInfo,
      //   serverResponse: err.serverResponse,
      //   // Additional debugging
      //   collectionPath: collection(db, TEAMS_COLLECTION).path,
      // });

      if (err.message.includes("Failed to verify")) {
        setError("Cannot access Firestore. Please check your permissions.");
      } else if (err.code === "permission-denied") {
        setError(`Permission denied. Please verify: 
          1. Collection path is correct (${TEAMS_COLLECTION})
          2. You are properly logged in (${!!user})
          3. Your email is verified (${user?.emailVerified})
          4. Your token is valid (exp: ${token?.expirationTime})`);
      } else if (err.message.includes("Invalid email")) {
        setError(err.message);
      } else {
        setError(`Failed to create team: ${err.message}`);
      }
    } finally {
      setLoading(false);
      setShowCreateForm(false);
      setTeamName("");
      setMembers([]);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold">Team Management</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-purple-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-purple-700"
        >
          <FaPlus /> {showCreateForm ? "Cancel" : "Create New Team"}
        </button>
      </div>

      {/* Create Team Form */}
      {showCreateForm && (
        <form
          onSubmit={handleCreateTeam}
          className="mb-8 p-6 bg-gray-50 rounded-lg space-y-6"
        >
          <h3 className="text-lg font-semibold mb-4">Create New Team</h3>

          {/* Team Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team Name
            </label>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter team name"
              disabled={loading}
            />
          </div>

          {/* Team Members */}
          <div className="space-y-3">
            <h4 className="font-semibold">Team Members</h4>

            {members.map((m, i) => (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
              >
                <input
                  type="email"
                  value={m.email}
                  onChange={(e) => updateMember(i, "email", e.target.value)}
                  placeholder="member@email.com"
                  className="md:col-span-7 p-2 border rounded w-full focus:ring-2 focus:ring-purple-500"
                  required
                />

                <select
                  value={m.role}
                  onChange={(e) => updateMember(i, "role", e.target.value)}
                  className="md:col-span-3 p-2 border rounded w-full"
                >
                  <option value="reader">Reader</option>
                  <option value="owner">Owner</option>
                </select>

                <button
                  type="button"
                  onClick={() => deleteMember(i)}
                  className="md:col-span-2 bg-red-50 hover:bg-red-100 text-red-600 rounded p-2 flex items-center justify-center"
                >
                  <FaTrashAlt />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={addEmptyMember}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-800"
            >
              <FaUserPlus /> Add Member
            </button>
          </div>

          {error && <div className="text-red-500 text-sm">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className="bg-purple-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-purple-700 disabled:opacity-50"
          >
            {loading ? (
              "Creating Team..."
            ) : (
              <>
                <FaPlus /> Create Team
              </>
            )}
          </button>
        </form>
      )}

      {/* Existing Teams List */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Your Teams</h3>
        {teams.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FaUserFriends className="mx-auto text-4xl mb-4" />
            <p>You haven't created any teams yet.</p>
            <p className="text-sm mt-2">
              Click "Create New Team" to get started.
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {teams.map((team) => (
              <div
                key={team.id}
                className="flex items-center justify-between p-4 bg-white rounded-lg shadow"
              >
                <div>
                  <h4 className="font-semibold">{team.name}</h4>
                  <p className="text-sm text-gray-500">
                    Created{" "}
                    {new Date(team.createdAt?.toDate()).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/ai-sdr/settings/team/${team.id}`)}
                  className="text-purple-600 hover:text-purple-800 flex items-center gap-2"
                >
                  <FaCog /> Manage Team
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateTeam;
